import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { AnalyticsProvider } from "./components/Analytics";
import { GA4Tracker } from "./components/GA4Tracker";
import { FlashLocationPopup } from "./components/FlashLocationOffer";
import { SocialProofBanner, StickyMobileCTA, ExitIntentPopup, WhatsAppFloat } from "./components/ConversionKit";

// Eager-loaded: Home (LCP critique) — toutes les autres pages sont lazy
import Home from "./pages/Home";
import { SHOW_SLUGS } from "./data/shows";

// Main pages — lazy
const Mascottes = lazy(() => import("./pages/Mascottes"));
const Anniversaire = lazy(() => import("./pages/Anniversaire"));
const Spectacles = lazy(() => import("./pages/Spectacles"));
const Location = lazy(() => import("./pages/LocationNew"));
const Animations = lazy(() => import("./pages/Animations"));
const Contact = lazy(() => import("./pages/Contact"));
const Tarifs = lazy(() => import("./pages/Tarifs"));
const LeLichou = lazy(() => import("./pages/LeLichou"));
// Legal — lazy
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const CGV = lazy(() => import("./pages/CGV"));
const PourquoiNousChoisir = lazy(() => import("./pages/PourquoiNousChoisir"));
const ArbreNoelEntrepriseBrest = lazy(() => import("./pages/ArbreNoelEntrepriseBrest"));
const LocationGlaceItalienne = lazy(() => import("./pages/LocationGlaceItalienne"));
const LocationGlaceItalienneVille = lazy(() => import("./pages/LocationGlaceItalienneVille"));
const LocationMachineVille = lazy(() => import("./pages/LocationMachineVille"));
const ArbreNoelEntrepriseVille = lazy(() => import("./pages/ArbreNoelEntrepriseVille"));
const SpectacleMagieVille = lazy(() => import("./pages/SpectacleMagieVille"));
const SpectacleMagieNiche = lazy(() => import("./pages/SpectacleMagieNiche"));
const NotFound = lazy(() => import("./pages/NotFound"));
// Blog — lazy
const Blog = lazy(() => import("./pages/Blog"));
const BlogFormulesAnniversaire = lazy(() => import("./pages/blog/BlogFormulesAnniversaire"));
const BlogMascottes = lazy(() => import("./pages/blog/BlogMascottes"));
const BlogSpectaclesEntreprise = lazy(() => import("./pages/blog/BlogSpectaclesEntreprise"));
const BlogNouvellesFormules = lazy(() => import("./pages/blog/BlogNouvellesFormules"));
const BlogLocationMachines = lazy(() => import("./pages/blog/BlogLocationMachines"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
// Admin — lazy (jamais charge pour visiteurs)
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminMessages = lazy(() => import("./pages/AdminMessages"));
const AdminSync = lazy(() => import("./pages/AdminSync"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSEO = lazy(() => import("./pages/AdminSEO"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const ShowPage = lazy(() => import("./pages/ShowPage"));
const AdminFlashLocation = lazy(() => import("./pages/AdminFlashLocation"));
const AdminStand = lazy(() => import("./pages/AdminStand"));
const AdminPlanning = lazy(() => import("./pages/AdminPlanning"));
// Dynamic SEO Templates — lazy (335 routes)
const SEOPageTemplate = lazy(() => import("./components/SEOPageTemplate"));
const LichouSEOTemplate = lazy(() => import("./components/LichouSEOTemplate"));

import { getAllSlugs } from "./data/seoPages";
import { getAllLichouSlugs } from "./data/lichouSeoPages";
import { GLACE_CITIES } from "./data/glaceCities";
import { MACHINES, MACHINE_CITIES } from "./data/machinesData";
import { ARBRE_NOEL_CITIES, SPECTACLE_MAGIE_CITIES } from "./data/spectacleCities";

const seoSlugs = getAllSlugs();
const lichouSlugs = getAllLichouSlugs();

// Fallback minimal pendant le chargement lazy (pas de spinner = aucun layout shift)
const PageFallback = () => <div style={{ minHeight: "60vh" }} aria-hidden="true" />;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsProvider>
            <GA4Tracker />
            <div className="min-h-screen flex flex-col">
              <SocialProofBanner />
              <Header />
              <FlashLocationPopup />
              <main className="flex-1 pb-20 lg:pb-0">
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    {/* Main pages */}
                    <Route path="/" element={<Home />} />
                    <Route path="/mascottes" element={<Mascottes />} />
                    <Route path="/anniversaire" element={<Anniversaire />} />
                    <Route path="/spectacles" element={<Spectacles />} />
                    {SHOW_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<ShowPage slug={s} />} />)}
                    <Route path="/location" element={<Location />} />
                    <Route path="/animations" element={<Animations />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/tarifs" element={<Tarifs />} />
                    <Route path="/loustic-breizh" element={<LeLichou />} />
                    <Route path="/le-lichou" element={<LeLichou />} />
                    {/* Legal */}
                    <Route path="/mentions-legales" element={<MentionsLegales />} />
                    <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                    <Route path="/cgv" element={<CGV />} />
                    <Route path="/conditions-generales-vente" element={<CGV />} />
                    <Route path="/pourquoi-nous-choisir" element={<PourquoiNousChoisir />} />
                    <Route path="/pourquoi-le-clown-magicien" element={<PourquoiNousChoisir />} />
                    <Route path="/arbre-de-noel-entreprise-brest" element={<ArbreNoelEntrepriseBrest />} />
                    <Route path="/location-glace-italienne" element={<LocationGlaceItalienne />} />
                    {Object.values(GLACE_CITIES).map((city) => (
                      <Route
                        key={`glace-${city.slug}`}
                        path={`/location-glace-italienne-${city.slug}`}
                        element={<LocationGlaceItalienneVille city={city} />}
                      />
                    ))}
                    {/* Location machines premium (24 pages) — Finistere Sud + Morbihan Lorient */}
                    {Object.values(MACHINES).flatMap((machine) =>
                      Object.values(MACHINE_CITIES).filter((city) => !city.machines || city.machines.includes(machine.slug)).map((city) => (
                        <Route
                          key={`${machine.slug}-${city.slug}`}
                          path={city.isPillar ? `/location-${machine.slug}` : `/location-${machine.slug}-${city.slug}`}
                          element={<LocationMachineVille machine={machine} city={city} />}
                        />
                      ))
                    )}
                    {/* Arbre de Noel Entreprise CSE (4 villes premium) */}
                    {Object.values(ARBRE_NOEL_CITIES).map((city) => (
                      <Route
                        key={`arbre-${city.slug}`}
                        path={`/arbre-de-noel-entreprise-${city.slug}`}
                        element={<ArbreNoelEntrepriseVille city={city} />}
                      />
                    ))}
                    {/* Spectacle Magie ville (4 villes premium) */}
                    {Object.values(SPECTACLE_MAGIE_CITIES).map((city) => (
                      <Route
                        key={`spectacle-${city.slug}`}
                        path={`/spectacle-magie-${city.slug}`}
                        element={<SpectacleMagieVille city={city} />}
                      />
                    ))}
                    {/* Spectacle Magie niches SEO (Festival Medieval / Rue / Mairie) */}
                    <Route path="/spectacle-magie-festival-medieval-bretagne" element={<SpectacleMagieNiche nicheKey="festival-medieval" />} />
                    <Route path="/spectacle-magie-rue-bretagne" element={<SpectacleMagieNiche nicheKey="spectacle-rue" />} />
                    <Route path="/spectacle-magie-mairie-comite-fetes-bretagne" element={<SpectacleMagieNiche nicheKey="mairie" />} />
                    {/* Blog */}
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/formules-anniversaire-enfant-bretagne" element={<BlogFormulesAnniversaire />} />
                    <Route path="/blog/mascottes-disponibles-finistere-morbihan" element={<BlogMascottes />} />
                    <Route path="/blog/spectacle-arbre-noel-entreprise-bretagne" element={<BlogSpectaclesEntreprise />} />
                    <Route path="/blog/location-barbe-papa-pop-corn-bretagne" element={<BlogLocationMachines />} />
                    <Route path="/blog/nouvelles-formules-2026" element={<BlogNouvellesFormules />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    <Route path="/admin/blog" element={<AdminBlog />} />
                    {/* Admin */}
                    <Route path="/admin" element={<AdminLogin />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/messages" element={<AdminMessages />} />
                    <Route path="/admin/sync" element={<AdminSync />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/admin/seo" element={<AdminSEO />} />
                    <Route path="/admin/reviews" element={<AdminReviews />} />
                    <Route path="/admin/flash-location" element={<AdminFlashLocation />} />
                    <Route path="/admin/stand" element={<AdminStand />} />
                    <Route path="/admin/planning" element={<AdminPlanning />} />
                    {/* Dynamic SEO pages (140+ pages from template) */}
                    {seoSlugs.map(slug => (
                      <Route key={slug} path={`/${slug}`} element={<SEOPageTemplate />} />
                    ))}
                    {/* Loustic Breizh SEO pages */}
                    {lichouSlugs.map(slug => (
                      <Route key={`lichou-${slug}`} path={`/${slug}`} element={<LichouSEOTemplate />} />
                    ))}
                    {/* 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <CookieConsent />
              <ExitIntentPopup />
              <WhatsAppFloat />
              <StickyMobileCTA />
              <Toaster position="top-right" />
            </div>
          </AnalyticsProvider>
        </BrowserRouter>
      </AuthProvider>
  );
}

export default App;
